"use client"

import { Button } from "@/components/ui/button"
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion"
import { format } from "date-fns"
import { Badge } from "@/components/ui/badge"
import { Loader2 } from "lucide-react"
import { useState } from "react"

export function ReviewSubmitForm({ formData, onPrevious, onSubmit, isLastStep }) {
  const [isSubmitting, setIsSubmitting] = useState(false)

  const handleSubmit = async () => {
    setIsSubmitting(true)
    try {
      await onSubmit()
    } catch (error) {
      console.error("Error submitting form:", error)
    } finally {
      setIsSubmitting(false)
    }
  }

  return (
    <div className="space-y-6">
      <div className="text-center mb-6">
        <h2 className="text-2xl font-bold">Review Your Request</h2>
        <p className="text-muted-foreground">Please review all the information before submitting</p>
      </div>

      <Accordion type="single" collapsible className="w-full">
        <AccordionItem value="basic-info">
          <AccordionTrigger>Basic Information</AccordionTrigger>
          <AccordionContent>
            <div className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <h4 className="font-medium text-sm">Title</h4>
                  <p>{formData.title}</p>
                </div>
                <div>
                  <h4 className="font-medium text-sm">Department</h4>
                  <p>{formData.department}</p>
                </div>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <h4 className="font-medium text-sm">Position</h4>
                  <p>{formData.position}</p>
                </div>
                <div>
                  <h4 className="font-medium text-sm">Number of Positions</h4>
                  <p>{formData.numberOfPositions}</p>
                </div>
              </div>
              <div>
                <h4 className="font-medium text-sm">Description</h4>
                <p className="whitespace-pre-wrap">{formData.description}</p>
              </div>
            </div>
          </AccordionContent>
        </AccordionItem>

        <AccordionItem value="requirements">
          <AccordionTrigger>Requirements</AccordionTrigger>
          <AccordionContent>
            <div className="space-y-4">
              {formData.requirements.length > 0 && (
                <div>
                  <h4 className="font-medium text-sm">Requirements</h4>
                  <ul className="list-disc pl-5 mt-2">
                    {formData.requirements.map((req, index) => (
                      <li key={index}>{req}</li>
                    ))}
                  </ul>
                </div>
              )}

              {formData.skills.length > 0 && (
                <div>
                  <h4 className="font-medium text-sm">Skills</h4>
                  <div className="flex flex-wrap gap-2 mt-2">
                    {formData.skills.map((skill, index) => (
                      <Badge key={index} variant="outline">
                        {skill}
                      </Badge>
                    ))}
                  </div>
                </div>
              )}

              {formData.techStacks.length > 0 && (
                <div>
                  <h4 className="font-medium text-sm">Tech Stacks</h4>
                  <div className="grid gap-2 mt-2">
                    {formData.techStacks.map((tech, index) => (
                      <div key={index} className="flex items-center gap-2">
                        <Badge>{tech.name}</Badge>
                        <span className="text-sm">
                          {tech.level} • {tech.yearsOfExperience} years
                        </span>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {formData.languages.length > 0 && (
                <div>
                  <h4 className="font-medium text-sm">Languages</h4>
                  <div className="grid gap-2 mt-2">
                    {formData.languages.map((lang, index) => (
                      <div key={index} className="flex items-center gap-2">
                        <Badge variant="outline">{lang.name}</Badge>
                        <span className="text-sm">{lang.proficiency}</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {formData.experience && (
                <div>
                  <h4 className="font-medium text-sm">Experience</h4>
                  <p className="whitespace-pre-wrap">{formData.experience}</p>
                </div>
              )}

              {formData.education && (
                <div>
                  <h4 className="font-medium text-sm">Education</h4>
                  <p className="whitespace-pre-wrap">{formData.education}</p>
                </div>
              )}
            </div>
          </AccordionContent>
        </AccordionItem>

        <AccordionItem value="working-conditions">
          <AccordionTrigger>Working Conditions</AccordionTrigger>
          <AccordionContent>
            <div className="space-y-4">
              <div>
                <h4 className="font-medium text-sm">Working Models</h4>
                <div className="flex flex-wrap gap-2 mt-2">
                  {formData.workingModels.map((model, index) => (
                    <Badge key={index} variant="secondary">
                      {model.charAt(0).toUpperCase() + model.slice(1)}
                    </Badge>
                  ))}
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <h4 className="font-medium text-sm">Gender Preference</h4>
                  <p className="capitalize">{formData.gender}</p>
                </div>

                {formData.workingTime && (
                  <div>
                    <h4 className="font-medium text-sm">Working Time</h4>
                    <p>{formData.workingTime}</p>
                  </div>
                )}
              </div>
            </div>
          </AccordionContent>
        </AccordionItem>

        <AccordionItem value="compensation-timeline">
          <AccordionTrigger>Compensation & Timeline</AccordionTrigger>
          <AccordionContent>
            <div className="space-y-4">
              <div>
                <h4 className="font-medium text-sm">Salary</h4>
                <p>
                  {formData.salary.min} - {formData.salary.max} {formData.salary.currency}
                </p>
                <p className="text-sm text-muted-foreground">Range: {formData.salary.range.replace(/_/g, " ")}</p>
              </div>

              {formData.budget > 0 && (
                <div>
                  <h4 className="font-medium text-sm">Budget</h4>
                  <p>
                    {formData.budget} {formData.salary.currency}
                  </p>
                </div>
              )}

              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                {formData.targetStartDate && (
                  <div>
                    <h4 className="font-medium text-sm">Target Start Date</h4>
                    <p>{format(new Date(formData.targetStartDate), "PPP")}</p>
                  </div>
                )}

                {formData.targetEndDate && (
                  <div>
                    <h4 className="font-medium text-sm">Target End Date</h4>
                    <p>{format(new Date(formData.targetEndDate), "PPP")}</p>
                  </div>
                )}

                {formData.requestDeadline && (
                  <div>
                    <h4 className="font-medium text-sm">Request Deadline</h4>
                    <p>{format(new Date(formData.requestDeadline), "PPP")}</p>
                  </div>
                )}
              </div>
            </div>
          </AccordionContent>
        </AccordionItem>

        <AccordionItem value="additional-info">
          <AccordionTrigger>Additional Information</AccordionTrigger>
          <AccordionContent>
            <div className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                {formData.projectCode && (
                  <div>
                    <h4 className="font-medium text-sm">Project Code</h4>
                    <p>{formData.projectCode}</p>
                  </div>
                )}

                <div>
                  <h4 className="font-medium text-sm">Priority</h4>
                  <p className="capitalize">{formData.priority}</p>
                </div>

                <div>
                  <h4 className="font-medium text-sm">Status</h4>
                  <p className="capitalize">{formData.status}</p>
                </div>
              </div>

              <div>
                <h4 className="font-medium text-sm">Requires Code Live Test</h4>
                <p>{formData.requiresCodeLiveTest ? "Yes" : "No"}</p>
              </div>

              {formData.notes && (
                <div>
                  <h4 className="font-medium text-sm">Additional Notes</h4>
                  <p className="whitespace-pre-wrap">{formData.notes}</p>
                </div>
              )}
            </div>
          </AccordionContent>
        </AccordionItem>
      </Accordion>

      <div className="flex justify-between pt-6">
        <Button type="button" variant="outline" onClick={onPrevious}>
          Previous
        </Button>
        <Button type="button" onClick={handleSubmit} disabled={isSubmitting}>
          {isSubmitting ? (
            <>
              <Loader2 className="mr-2 h-4 w-4 animate-spin" />
              Submitting
            </>
          ) : (
            "Submit Request"
          )}
        </Button>
      </div>
    </div>
  )
}

